import React, { memo, useState, useEffect, useRef, FC } from 'react';
import * as eCharts from 'echarts';
import { Segment } from 'tea-component';
import { mathFormat } from '@/pages/intelligence/utils/commonUtils';
import { lineTooltipFormatter } from '../Retention/linechart';
import { useIntl, getLocale } from 'umi';

interface RatiosRegionBarProps {
  ratiosData: any;
}

const RatiosRegionBar: FC<RatiosRegionBarProps> = (props) => {
  let myChart: any = null;
  const chartRef = useRef(null);
  const [options, setOptions] = useState<any>();
  const { ratiosData = {} } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [activeSegment, setActiveSegment] = useState('lty');

  const segmentList = [
    { text: formatMessage({ id: 'dbi_benchmark_ltv_365' }), value: 'lty' },
    {
      text: formatMessage({ id: 'dbi_benchmark_relative' }),
      value: 'cpi',
    },
    {
      text: formatMessage({ id: 'dbi_benchmark_register' }),
      value: 'register',
    },
  ];

  const handleSegmentChange = (value: string) => {
    setActiveSegment(value);
  };

  useEffect(() => {
    const maxLimit = activeSegment !== 'lty' ? { max: 100 } : {};

    const seriesData = ratiosData?.[activeSegment] || [];
    const xAxisList = seriesData?.map((item) =>
      locale === 'zh-CN' ? item?.date_cn : item?.date,
    );
    const targetSeriesData = seriesData?.map((item) =>
      activeSegment === 'lty' ? item?.value : item?.value * 100,
    );

    const maxValue = Math.max(...seriesData?.map((item) => item.value));

    const titleNow =
      segmentList?.find((item) => item?.value === activeSegment)?.text || '';
    const regionOptions = {
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        extraCssText: 'border: none;',
        padding: 0,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          label: {
            show: false,
            color: '#3B3D4B',
            backgroundColor: '#FFF',
            formatter: ({ value }) => {
              if (value === 'China(HK,MO,TW)') {
                return 'China\n(HK,MO,TW)';
              } else {
                const genreXlabelWidth = 1560;
                const _value = value?.split(' ');
                const len = _value.length;
                const lenFisrt = _value[0].length;
                if (len === 1) {
                  if (value.length > 4) {
                    return `${value.slice(0, lenFisrt)}\n
                      ${value.slice(lenFisrt + 1)}`;
                  }
                  return value;
                }
                if (document.body.clientWidth >= 1910) {
                  return `${_value[0]}\n${
                    len === 2 ? _value[1] : `${_value[1]}...`
                  }`;
                }
                if (document.body.clientWidth > genreXlabelWidth) {
                  return lenFisrt > 7
                    ? `${value.slice(0, 7)}\n${value.slice(8, 14)}${
                        value.length > 14 ? '...' : ''
                      }`
                    : `${_value[0]}\n${
                        len === 2 ? _value[1] : `${_value[1]}...`
                      }`;
                }
                if (value.length > 4) {
                  return `${value.slice(0, lenFisrt)}\n${value.slice(
                    lenFisrt + 1,
                  )}`;
                }

                return value;
              }
            },
          },
        },
        formatter: (param: any[]) => {
          return lineTooltipFormatter(
            param?.sort((a, b) => b?.value - a?.value),
            param[0]?.axisValue,
            '',
            (value: any) =>
              activeSegment !== 'lty'
                ? `${value?.toFixed(2) || value}%`
                : value < 1000
                ? value?.toFixed(2)
                : `${mathFormat(value, true)}`,
          );
        },
        appendToBody: true,
      },
      legend: {
        show: true,
        icon: 'circle',
        type: 'scroll',
        selectedMode: true,
        textStyle: {
          fontSize: 12,
          color: '#888',
        },
        pageIconSize: 10,
        pageTextStyle: {
          color: '#707070',
        },
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 20,
        bottom: -4,
      },
      grid: {
        left: 2,
        right: '0',
        bottom: locale === 'zh-CN' ? '25px' : '20px',
        top: 20,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisList,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          margin: 10,
          interval: 0,
          showMaxLabel: true,
          showMinLabel: true,
          color: '#000',
          opacity: 0.45,
          formatter: (value) => {
            if (value === 'China(HK,MO,TW)') {
              return 'China\n(HK,MO,TW)';
            } else {
              const genreXlabelWidth = 1560;
              const _value = value?.split(' ');
              const len = _value.length;
              const lenFisrt = _value[0].length;
              if (len === 1) {
                if (value.length > 4) {
                  return `${value.slice(0, lenFisrt)}\n
                    ${value.slice(lenFisrt + 1)}`;
                }
                return value;
              }
              if (document.body.clientWidth >= 1910) {
                return `${_value[0]}\n${
                  len === 2 ? _value[1] : `${_value[1]}...`
                }`;
              }
              if (document.body.clientWidth > genreXlabelWidth) {
                return lenFisrt > 7
                  ? `${value.slice(0, 7)}\n${value.slice(8, 14)}${
                      value.length > 14 ? '...' : ''
                    }`
                  : `${_value[0]}\n${
                      len === 2 ? _value[1] : `${_value[1]}...`
                    }`;
              }
              if (value.length > 4) {
                return `${value.slice(0, lenFisrt)}\n${value.slice(
                  lenFisrt + 1,
                )}`;
              }
              return value;
            }
          },
        },
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: {
            color: '#BFBFBF',
            width: 1,
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
            },
          },
          splitNumber: activeSegment === 'lty' ? 3 : 5,
          axisLabel: {
            color: '#000',
            opacity: 0.45,
            formatter: (value, index) => {
              if (activeSegment === 'lty') {
                if (value >= maxValue) {
                  return value < 1
                    ? `$${value}`
                    : `$${mathFormat(value, true)}`;
                } else {
                  return value < 1 ? value : `${mathFormat(value, true)}`;
                }
              } else {
                return `${value}%`;
              }
            },
          },
          ...maxLimit,
        },
        // {
        //   type: 'value',
        //   splitLine: {
        //     show: false,
        //   },
        //   axisLabel: {},
        //   splitNumber: 3,
        // },
      ],
      color: ['#5086F3', '#5EC8F1'],
      series: [
        {
          name: titleNow,
          type: 'bar',
          barMaxWidth: 60,
          barCategoryGap: '20%',
          data: targetSeriesData,
        },
      ],
    };
    setOptions(regionOptions);
  }, [activeSegment, locale]);

  useEffect(() => {
    if (chartRef?.current) {
      if (myChart === null) {
        myChart = eCharts.init(chartRef!.current!);
      }
      options && myChart.setOption(options, true);
    } else {
      if (myChart) {
        myChart.dispose();
      }
    }
  }, [options, chartRef]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };

  return (
    <div>
      <Segment
        options={segmentList}
        value={activeSegment}
        onChange={handleSegmentChange}
      />

      {(ratiosData?.[activeSegment] || [])?.length > 0 ? (
        <div style={{ height: 288, width: '100%' }} ref={chartRef}></div>
      ) : null}
      {!(ratiosData?.[activeSegment] || [])?.length && (
        <div className="revenue-nodata-container key-ratio-no-data">
          {formatMessage({ id: 'dbi_detail_no_data_under' })}
        </div>
      )}
    </div>
  );
};

export default memo(RatiosRegionBar);
